import React from 'react'
import { useFitStore } from '../store/useFitStore'
import { Link } from 'react-router-dom'
import Card from '../components/shared/Card'
import Button from '../components/shared/Button'

const ClientDashboard: React.FC = () => {
  const { user, sessions, updateHabit, clients, workoutLogs } = useFitStore();

  const clientData = clients.find(c => c.id === user?.id);
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // Today's training = the session the coach programmed for this client today.
  const todaySession = sessions.find((s) => s.clientId === user?.id && s.date === today);
  const exerciseCount = todaySession?.sections.reduce((n, sec) => n + sec.exercises.length, 0) ?? 0;
  const habits = ['Hydration Goal', 'Daily Steps', 'Protein Target'];

  const handleToggleHabit = (habitName: string) => {
    if (!user) return;
    const isCompleted = clientData?.habits?.[today]?.[habitName] || false;
    updateHabit(user.id, today, habitName, !isCompleted);
  };

  return (
    <div className="main-content no-scrollbar">

      <header className="mb-8 mt-2">
        <p className="text-fit-muted text-xs font-medium tracking-[1.5px] uppercase mb-1">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
        <h1 className="hero text-white text-[42px] leading-[0.9] font-bold tracking-[-0.03em]">
          Welcome, {user?.name.split(' ')[0]}.
        </h1>
        <p className="text-white text-[17px] font-semibold tracking-[-0.01em] mt-1">Ready to train?</p>
      </header>

      {/* PROGRESS MOSAIC */}
      <div className="stats-grid" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <Card variant="glass" padding="var(--spacing-md)" style={{ borderLeft: '4px solid var(--accent-primary)' }}>
          <p className="text-muted" style={{ fontSize: '0.65rem', fontWeight: 800 }}>WORKOUTS</p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <span style={{ fontSize: '2rem', fontWeight: 900 }}>{workoutLogs.length}</span>
            <span className="text-accent" style={{ fontSize: '0.8rem', fontWeight: 700 }}>total</span>
          </div>
        </Card>
        <Card variant="glass" padding="var(--spacing-md)" style={{ borderLeft: '4px solid var(--accent-secondary)' }}>
          <p className="text-muted" style={{ fontSize: '0.65rem', fontWeight: 800 }}>STREAK</p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <span style={{ fontSize: '2rem', fontWeight: 900 }}>12</span>
            <span style={{ color: 'var(--accent-secondary)', fontSize: '0.8rem', fontWeight: 700 }}>days</span>
          </div>
        </Card>
      </div>

      {/* HERO WORKOUT CARD */}
      <section style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h2 style={{ marginBottom: 'var(--spacing-md)', fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Today's Mission</h2>
        <Card 
          style={{ 
            background: 'linear-gradient(135deg, var(--bg-secondary) 0%, #1e1b4b 100%)',
            border: '1px solid rgba(74, 222, 128, 0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative background shape */}
          <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', background: 'var(--accent-primary)', opacity: 0.05, borderRadius: '50%' }}></div>
          
          {todaySession ? (
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h3 style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: '4px' }}>{todaySession.title || "Today's Session"}</h3>
              <p className="text-muted" style={{ fontSize: '0.9rem', marginBottom: 'var(--spacing-lg)' }}>
                {exerciseCount} Exercise{exerciseCount === 1 ? '' : 's'} • <span className="text-accent">
                  {todaySession.status === 'completed' ? 'Completed' : todaySession.status === 'partial' ? 'In progress' : 'Ready'}
                </span>
              </p>
              <Link to="/workout/live" style={{ textDecoration: 'none' }}>
                <Button variant="primary" fullWidth size="lg" className="glow-primary">
                  {todaySession.status === 'completed' ? 'REVIEW SESSION' : todaySession.status === 'partial' ? 'CONTINUE SESSION' : 'START SESSION'}
                </Button>
              </Link>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-md)' }}>
              <p className="text-muted">No session scheduled today.</p>
            </div>
          )}
        </Card>
      </section>

      {/* HABIT TRACKER GRID */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
          <h2 style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Daily Habits</h2>
          <span style={{ fontSize: '0.7rem', color: 'var(--accent-primary)', fontWeight: 800 }}>
            {Object.values(clientData?.habits?.[today] || {}).filter(Boolean).length}/{habits.length}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
          {habits.map((habit) => {
            const isDone = clientData?.habits?.[today]?.[habit] || false;
            return (
              <Card 
                key={habit} 
                variant="glass"
                padding="var(--spacing-md)"
                onClick={() => handleToggleHabit(habit)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-md)',
                  border: isDone ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid var(--glass-border)',
                  transform: isDone ? 'scale(0.98)' : 'scale(1)',
                  opacity: isDone ? 0.8 : 1
                }}
              >
                <div style={{
                  width: '28px',
                  height: '28px',
                  border: isDone ? 'none' : '2px solid var(--bg-tertiary)',
                  background: isDone ? 'var(--accent-primary)' : 'transparent',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isDone ? '0 0 10px var(--accent-glow)' : 'none'
                }}>
                  {isDone && <span style={{ color: 'var(--bg-primary)', fontWeight: 900, fontSize: '0.9rem' }}>✓</span>}
                </div>
                <span style={{ fontWeight: 700, color: isDone ? 'var(--accent-primary)' : 'white', fontSize: '1rem' }}>{habit}</span>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  )
}

export default ClientDashboard
